import { Error, Paging } from "api/baseApiResponseModel/BaseApiResponseModel";
import { CarrierListRequestModel } from "api/merchantRepository/filterFeature/model/carrierList/CarrierListRequestModel";
import { CarrierListResponseModel } from "api/merchantRepository/filterFeature/model/carrierList/CarrierListResponseModel";
import { PackageListRequestModel } from "api/merchantRepository/filterFeature/model/packageList/PackageListRequestModel";
import { PackageListResponseModel } from "api/merchantRepository/filterFeature/model/packageList/PackageListResponseModel";
import { MobiSimRegistrationRequestModel } from "api/merchantRepository/mobiSimRegistration/model/MobiSimRegistrationRequestModel";
import { MobiSimRegistrationResponseModel } from "api/merchantRepository/mobiSimRegistration/model/MobiSimRegistrationResponseModel";
import { defaultStoreRepository } from "api/repositories/store/StoreRepository";
import { defaultUserManagementRepository } from "api/repositories/userManagement/UserManagementRepository";
import { BaseViewModel } from "components/base/BaseViewModel";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { strings } from "utils/localizedStrings";
import { ServiceRepo } from "utils/serviceRepo/ServiceRepo";

class MobiSimRegistrationInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public statusLoading = new BehaviorSubject<Record<string, boolean>>({});
  public fetchList = new BehaviorSubject<MobiSimRegistrationRequestModel>(
    new MobiSimRegistrationRequestModel()
  );
  public fetchCarrier = new BehaviorSubject<CarrierListRequestModel>(
    new CarrierListRequestModel()
  );
  public fetchPackage = new BehaviorSubject<PackageListRequestModel>(
    new PackageListRequestModel()
  );
  public exportExcel = new BehaviorSubject<MobiSimRegistrationRequestModel>(
    new MobiSimRegistrationRequestModel()
  );
  public fetchDetail = new BehaviorSubject<MobiSimRegistrationRequestModel>(
    new MobiSimRegistrationRequestModel()
  );
  public checkStatus = new BehaviorSubject<string>("");
  public loadStores = new BehaviorSubject<{ userType: number }>({
    userType: 0,
  });
  public loadStaff = new BehaviorSubject<{
    selectedStore: string;
    userType: number;
  }>({ selectedStore: "", userType: 0 });

  [Symbol.dispose](): void {
    this.loading.complete();
    this.detailLoading.complete();
    this.fetchList.complete();
    this.fetchPackage.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.checkStatus.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
  }
}

class MobiSimRegistrationOuput implements Disposable {
  public savedList = new BehaviorSubject<
    Array<MobiSimRegistrationResponseModel>
  >([]);
  public savedCarrier = new BehaviorSubject<Array<CarrierListResponseModel>>(
    []
  );
  public savedDetail = new BehaviorSubject<MobiSimRegistrationResponseModel>(
    {}
  );
  public savedPackage = new BehaviorSubject<Array<PackageListResponseModel>>(
    []
  );
  public pagination = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<boolean>(false);
  public onCheckStatusSuccess = new BehaviorSubject<boolean>(false);
  public storeOptions = new BehaviorSubject<
    Array<{ label: string; value: string }>
  >([{ label: strings.GlobalLabels.All, value: "" }]);
  public staffOptions = new BehaviorSubject<
    Array<{ label: string; value: string }>
  >([{ label: strings.GlobalLabels.All, value: "" }]);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.savedCarrier.complete();
    this.savedPackage.complete();
    this.pagination.complete();
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onCheckStatusSuccess.complete();
    this.storeOptions.complete();
    this.staffOptions.complete();
  }
}

@injectable()
export class MobiSimRegistrationViewModel extends BaseViewModel<
  MobiSimRegistrationInput,
  MobiSimRegistrationOuput
> {
  constructor() {
    super(new MobiSimRegistrationInput(), new MobiSimRegistrationOuput());
  }
  private repo = ServiceRepo.getMobiSimRegistrationRepo();
  private filterRepo = ServiceRepo.getFilterFeatureRepo();

  protected override transform(): void {
    this.input.fetchList
      .subscribe(async (params) => {
        if (isEmpty(params)) {
          return;
        } else {
          try {
            this.input.loading.next(true);
            const res = await this.repo.getList(params);
            if (res?.data) {
              this.output.savedList.next(res?.data ?? []);
              this.output.pagination.next(res?.pagination);
            }
          } catch (error) {
            console.error(error);
            this.output.savedList.next([]);
            this.output.onError.next(error);
          } finally {
            this.input.loading.next(false);
          }
        }
      })
      .add(this.subscriptions);

    this.input.fetchCarrier
      .subscribe(async (params) => {
        if (isEmpty(params)) {
          return;
        } else {
          try {
            const res = await this.filterRepo.getCarrierList(params);
            if (res?.data) {
              let finalData = res?.data?.filter(
                (child) =>
                  child?.code === "viettel" ||
                  child?.code === "mobifone" ||
                  child?.code === "vinaphone"
              );
              this.output.savedCarrier.next(finalData);
            }
          } catch (error) {
            console.error(error);
          }
        }
      })
      .add(this.subscriptions);

    this.input.fetchPackage
      .subscribe(async (params) => {
        if (isEmpty(params)) {
          return;
        } else {
          const cache = productCache?.getCache(CacheKeyEnum.simMhubPackage);
          if (!isEmpty(cache)) {
            this.output.savedPackage.next(cache);
          } else {
            try {
              const res = await this.filterRepo.getPackageList(params);
              if (res?.data) {
                this.output.savedPackage.next(res?.data);
                productCache?.saveCache(res?.data, CacheKeyEnum.simMhubPackage);
              }
            } catch (error) {
              console.error(error);
            }
          }
        }
      })
      .add(this.subscriptions);

    this.input.fetchDetail
      .subscribe(async (params) => {
        if (isEmpty(params)) {
          return;
        } else {
          try {
            this.input.detailLoading.next(true);
            const res = await this.repo.getDetail(params);
            if (res?.data) {
              this.output.savedDetail.next(res?.data);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.detailLoading.next(false);
          }
        }
      })
      .add(this.subscriptions);

    this.input.exportExcel
      .subscribe(async (params) => {
        if (isEmpty(params)) {
          return;
        } else {
          try {
            this.input.loading.next(true);
            const res = await this.repo.exportReport(params);
            if (res?.data) {
              const url = window.URL.createObjectURL(res?.data as Blob);
              const link = document.createElement("a");
              const timestamp = dayjs().format("YYYYMMDD_HHmmss");
              link.href = url;
              link.setAttribute("download", `SIM_REPORT_${timestamp}.xlsx`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
              this.output.onExportSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.loading.next(false);
          }
        }
      })
      .add(this.subscriptions);

    this.input.checkStatus
      .subscribe(async (code) => {
        if (isEmpty(code)) {
          return;
        } else {
          try {
            this.input.statusLoading.next({ [code]: true });
            const res = await this.repo.checkStatus(code);
            if (res?.data) {
              this.output.onCheckStatusSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.statusLoading.next({ [code]: false });
          }
        }
      })
      .add(this.subscriptions);

    this.input.loadStores
      .subscribe(async ({ userType }) => {
        try {
          const cache = productCache.getCache(CacheKeyEnum.mhubStoreList);
          if (userType === 3) {
            if (isEmpty(cache)) {
              const res = await defaultStoreRepository.getStoreList({
                page: 0,
                limit: 500,
              } as any);
              const items = res?.data?.data?.items || [];
              const opts = [
                { label: strings.GlobalLabels.All, value: "" },
                ...items.map((s) => ({
                  label: s?.name || s?.code,
                  value: s?.userId,
                  code: s?.code,
                })),
              ];
              productCache.saveCache(
                opts as unknown as Record<string, unknown>,
                CacheKeyEnum.mhubStoreList
              );
              this.output.storeOptions.next(opts);
            } else {
              this.output.storeOptions.next(cache);
            }
          }
        } catch (e) {
          this.output.storeOptions.next([
            { label: strings.GlobalLabels.All, value: "" },
          ]);
        }
      })
      .add(this.subscriptions);

    this.input.loadStaff
      .subscribe(async ({ selectedStore, userType }) => {
        let isAgency = userType === 3 && !isEmpty(selectedStore);
        let isStore = userType === 4;
        if (isEmpty(selectedStore) && userType === 3) {
          this.output.staffOptions.next([
            {
              label: strings.GlobalMessage.InvalidStore,
              value: "",
            },
          ]);
        } else {
          try {
            if (isAgency || isStore) {
              const res = await defaultUserManagementRepository.getUserListV2({
                page: 0,
                limit: 500,
                ...(selectedStore ? { store: selectedStore } : {}),
              } as any);
              const items = res?.data?.items || [];
              const opts = [
                { label: strings.GlobalLabels.All, value: "" },
                ...items.map((u) => ({
                  label: u?.fullName || u?.userName || u?.phone,
                  value: u?.id,
                })),
              ];
              this.output.staffOptions.next(opts);
            }
          } catch (e) {
            this.output.staffOptions.next([
              { label: strings.GlobalLabels.All, value: "" },
            ]);
          }
        }
      })
      .add(this.subscriptions);
  }
}
